import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FontDownloadIcon from "@mui/icons-material/FontDownload";
import ImageIcon from "@mui/icons-material/Image";
import PhotoSizeSelectLargeIcon from "@mui/icons-material/PhotoSizeSelectLarge";
import BrushIcon from "@mui/icons-material/Brush";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import FormatShapesIcon from "@mui/icons-material/FormatShapes";
import TwoKIcon from "@mui/icons-material/TwoK";
import FourKIcon from "@mui/icons-material/FourK";
import RouteIcon from "@mui/icons-material/Route";
import { useTranslation } from "react-i18next";
import { useDocument } from "../../../../hooks/useDocument";
import { Images } from "../../../../assets";
import { ColumnCenteredBox, RowBox } from "../../../../utils/styledBox";
import { alpha } from "@mui/material/styles";
import { useCustomNavigate } from "../../../../hooks/useCustomNavigate";
import VipLabel from "../../../../components/common/VipLabel";
import FilterBAndWIcon from "@mui/icons-material/FilterBAndW";

interface ModelDetailModalProps {
  open: boolean;
  onClose: () => void;
  model: {
    value: string;
    label: string;
    description: string;
    category?: string[];
    type?: string[];
  };
  isVideo?: boolean;
}

const ModelDetailModal: React.FC<ModelDetailModalProps> = ({ open, onClose, model, isVideo = false }) => {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();

  // モデル名からMDファイルのパスを導出 (例: DallE3 → models/dalle3)
  const modelKey = model.value.toLowerCase();
  const documentPath = `models/${modelKey}`;

  // useDocumentフックを使用してマークダウンを取得
  const { document, isLoading, error } = useDocument(documentPath);

  const handleNavigate = () => {
    if (model.type && model.type.length > 0) {
      navigate(`/juno/image-generator?menu=${model.type[0]}&ai_engine=${model.value}`);
    } else if (isVideo) {
      navigate("/juno/video-generator", { state: { aiEngine: model.value } });
    }
    onClose();
  };

  const handleSearchNavigate = () => {
    navigate(`/juno?ai_engine=${model.value}`);
    onClose();
  };

  // モデル画像の取得
  const modelImage = isVideo
    ? Images.company[model.value.toLowerCase() as keyof typeof Images.company] || Images.noImage
    : Images.juno.aiEngine[model.value as keyof typeof Images.juno.aiEngine] || Images.noImage;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
          backdropFilter: "blur(10px)",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {(model.category?.includes("pro") || isVideo) && <VipLabel />}
          <Typography variant="h6" component="div">
            {model.label}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ position: "relative" }}>
        {/* モデル画像 */}
        <img
          src={modelImage}
          alt={model.label}
          style={{
            borderRadius: "4px",
            maxHeight: "120px",
            width: "200px",
            objectFit: "cover",
            position: "absolute",
            top: 16,
            right: 16,
          }}
        />

        {/* 対応機能アイコン - 画像モデルのみ表示 */}
        {!isVideo && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component={"h5"} gutterBottom>
              {t("juno.modal.supportedFeatures")}
            </Typography>
            <RowBox gap={2} sx={{ flexWrap: "wrap" }}>
              {/* Text to Image */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={t("juno.modal.textToImage")} arrow placement="top">
                  <FontDownloadIcon
                    sx={{
                      opacity: model.type?.includes("txt2img") ? 1 : 0.3,
                      color: "#fff",
                      backgroundColor: model.type?.includes("txt2img") ? "#036c03" : "#999",
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  />
                </Tooltip>
                <Typography variant="body2">{t("juno.modal.textToImage")}</Typography>
              </Box>

              {/* Image to Image */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={t("juno.modal.imageToImage")} arrow placement="top">
                  <ImageIcon
                    sx={{
                      opacity: model.type?.includes("img2img") ? 1 : 0.3,
                      color: "#fff",
                      backgroundColor: model.type?.includes("img2img") ? "#0b7780" : "#999",
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  />
                </Tooltip>
                <Typography variant="body2">{t("juno.modal.imageToImage")}</Typography>
              </Box>

              {/* Upscale */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={t("juno.modal.upscale")} arrow placement="top">
                  <PhotoSizeSelectLargeIcon
                    sx={{
                      opacity: model.type?.includes("upscale") ? 1 : 0.3,
                      color: "#fff",
                      backgroundColor: model.type?.includes("upscale") ? "#800b0b" : "#999",
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  />
                </Tooltip>
                <Typography variant="body2">{t("juno.modal.upscale")}</Typography>
              </Box>

              {/* Editor */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={t("juno.modal.editor")} arrow placement="top">
                  <FilterBAndWIcon
                    sx={{
                      opacity: model.type?.includes("editor") ? 1 : 0.3,
                      color: "#fff",
                      backgroundColor: model.type?.includes("editor") ? "#80570b" : "#999",
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  />
                </Tooltip>
                <Typography variant="body2">{t("juno.modal.editor")}</Typography>
              </Box>
            </RowBox>
          </Box>
        )}

        {/* カテゴリアイコン - 画像モデルのみ表示 */}
        {!isVideo && model.category && model.category.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t("juno.modal.categories")}
            </Typography>
            <RowBox gap={2} sx={{ flexWrap: "wrap" }}>
              {/* Juno */}
              {model.category?.includes("juno") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.juno")} arrow placement="top">
                    <ColumnCenteredBox
                      sx={{
                        backgroundColor: "#3273a8",
                        borderRadius: 1,
                        p: 0.5,
                        color: "#fff",
                        width: 24,
                        height: 24,
                      }}
                    >
                      <Typography variant="caption" fontWeight={"bold"}>
                        J
                      </Typography>
                    </ColumnCenteredBox>
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.juno")}</Typography>
                </Box>
              )}

              {/* Vector */}
              {model.category?.includes("vector") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.vector")} arrow placement="top">
                    <FormatShapesIcon
                      sx={{
                        color: "#fff",
                        backgroundColor: "#036c03",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.vector")}</Typography>
                </Box>
              )}

              {/* 2K */}
              {model.category?.includes("2k") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.twoK")} arrow placement="top">
                    <TwoKIcon
                      sx={{
                        color: "#fff",
                        backgroundColor: "#0b7780",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.twoK")}</Typography>
                </Box>
              )}

              {/* 4K */}
              {model.category?.includes("4k") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.fourK")} arrow placement="top">
                    <FourKIcon
                      sx={{
                        color: "#fff",
                        backgroundColor: "#1565c0",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.fourK")}</Typography>
                </Box>
              )}

              {/* Vary */}
              {model.category?.includes("vary") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.vary")} arrow placement="top">
                    <AltRouteIcon
                      sx={{
                        color: "#fff",
                        backgroundColor: "#4d2464",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.vary")}</Typography>
                </Box>
              )}

              {/* ControlNet */}
              {model.category?.includes("controlNet") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.controlNet")} arrow placement="top">
                    <RouteIcon
                      sx={{
                        color: "#fff",
                        backgroundColor: "#ce6e00",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.controlNet")}</Typography>
                </Box>
              )}

              {/* Brush */}
              {model.category?.includes("brush") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip title={t("juno.input.category.brush")} arrow placement="top">
                    <BrushIcon
                      sx={{
                        color: "#fff",
                        backgroundColor: "#800b0b",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                  <Typography variant="body2">{t("juno.input.category.brush")}</Typography>
                </Box>
              )}
            </RowBox>
          </Box>
        )}

        {/* 詳細情報 - Markdownコンテンツまたはモデル説明 */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component={"h5"} gutterBottom>
              {t("juno.modal.additionalInfo")}
            </Typography>
            <DialogContentText>{model.description}</DialogContentText>
          </Box>
        ) : document ? (
          <Box>
            <Typography variant="h6" component={"h5"} gutterBottom>
              {t("juno.modal.additionalInfo")}
            </Typography>
            <DialogContentText
              component="div"
              dangerouslySetInnerHTML={{ __html: document }}
              sx={{ "& h6": { m: 0 } }}
            />
          </Box>
        ) : (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component={"h5"} gutterBottom>
              {t("juno.modal.additionalInfo")}
            </Typography>
            <DialogContentText>{model.description}</DialogContentText>
          </Box>
        )}

        {/* サンプル画像 - 画像モデルのみ表示 */}
        {/* {!isVideo && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <img
                src={Images.juno.aiEngine[model.value as keyof typeof Images.juno.aiEngine] || Images.noImage}
                alt={`${model.label} sample`}
                style={{ maxWidth: "100%", borderRadius: "4px", maxHeight: "300px" }}
              />
            </Box>
          </Box>
        )} */}
      </DialogContent>
      <DialogActions>
        {/* 検索結果ボタン - 画像モデルのみ表示 */}
        {!isVideo && (
          <Button variant="outlined" color="primary" onClick={handleSearchNavigate} disableElevation>
            {t("juno.modal.searchResults")}
          </Button>
        )}
        <Button variant="contained" color="primary" onClick={handleNavigate} disableElevation>
          {t("juno.modal.useModel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelDetailModal;
